import { Injectable, OnDestroy } from '@angular/core';

import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators';

import {
    AdvancedSearchGroup,
    ApiQuery,
    ApiQueryBuilder,
    EmptyStructuredQuery,
    Facet,
    FacetRequest,
    FacetValueOptions,
    LogicalOperand,
    Operand,
    Restriction,
    ShFacetDataService,
    StructuredQuery
} from '@stylehaven/components';

import { ApiFragment } from '../entities/api-fragment.entity';
import { DottedKey, MetadataField } from '../entities/metadata-field.entity';
import { MediaObjectId, RecordType } from '../entities/metadata.entity';
import { RecordSearch } from '../entities/record-search.entity';
import { LOADING_STUK_COUNT, StukCount, UNKNOWN_STUK_COUNT } from '../entities/stuk-count.entity';
import { collectSearchResults, MAX_COLLECTED_RESULTS } from '../helpers/paged-search.helper';
import { RecordSandboxService } from '../sandbox/record-sandbox.service';
import { HierarchyService } from './hierarchy.service';

/** Per dossier id, how many stukken it contributes. `null` stands for an answer that cannot be trusted. */
type ChildCounts = ReadonlyMap<string, number> | null;

/** `ApiStructuredQuery` is not part of the vendor's public entry point, but the request that carries it is. */
type FacetStructuredQuery = FacetRequest['Search']['StructuredQuery'];

/**
 * HFBD-7a: the `Aantal stukken` column's data.
 *
 * A dossier's child count is stored nowhere, so it is counted — for the whole page at once rather than per
 * row. A stuk reaches its dossier along either of two paths, and the two are counted separately so that a
 * stuk which somehow travels both can never be counted twice:
 *
 * - **attached** children come from one *facet* request grouping `Structural.ReferenceCodes.Dossier` over a
 *   query that excludes candidates, so one request buckets every dossier on screen;
 * - **candidate** children come from one ordinary search returning the records themselves, which is what
 *   lets them be grouped by parent client-side instead of summed blind.
 *
 * The count therefore matches what expanding actually reveals, which is the whole point of the column — see
 * {@link HierarchyService} for the query the expansion itself runs.
 *
 * The candidate half is shared with the export scope — see {@link candidateChildIds}.
 */
@Injectable()
export class StukCountService implements OnDestroy {
    /**
     * Where paging stops: past this many candidate children on one page of dossiers, counting them costs more
     * than the column is worth, and the column says nothing rather than something short. A candidate is a
     * transient pre-acceptance state, so reaching this at all would be remarkable.
     */
    static readonly MAX_CANDIDATES: number = MAX_COLLECTED_RESULTS;

    /**
     * Every way this column can fail ends in the same silent blank cell — indistinguishable from a row that is
     * not a dossier. One warning per page (never per row) is what makes the difference visible in the console.
     */
    private static readonly LOG_PREFIX: string = 'Aantal stukken (HFBD-7a):';

    private readonly destroy$: Subject<void> = new Subject<void>();

    private readonly counts$: BehaviorSubject<ReadonlyMap<string, StukCount>> = new BehaviorSubject<
        ReadonlyMap<string, StukCount>
    >(new Map());

    /** What the counts already taken were scoped by. A count taken under other restrictions is a different one. */
    private restrictions?: Restriction;

    constructor(
        private readonly sandbox: RecordSandboxService,
        private readonly facetDataService: ShFacetDataService
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.counts$.complete();
    }

    /**
     * What a cell renders. Defaults to `unknown` — rendering nothing — because the column sits on every
     * top-level row, including the weesdocumenten and stukken that have no children to count.
     */
    countFor(dossierId: string): Observable<StukCount> {
        return this.counts$.pipe(
            map(counts => counts.get(dossierId) ?? UNKNOWN_STUK_COUNT),
            distinctUntilChanged()
        );
    }

    /**
     * Two requests for a whole page of dossiers. A dossier already counted is skipped — but one left
     * `unknown` is asked about again, so the next page emission repairs a batch that failed.
     *
     * `restrictions` are whatever the tree itself runs under, carried for the same reason
     * {@link HierarchyService} carries them: a count taken in a different scope than the expansion below it
     * would promise rows that expansion then refuses to show. They reach a page asynchronously, so counts
     * taken before they landed are discarded rather than left over-reporting.
     */
    load(dossierIds: string[], restrictions?: Restriction): void {
        if (!isEqual(restrictions, this.restrictions)) {
            this.restrictions = restrictions;
            this.reset();
        }

        const listed: Set<string> = new Set(dossierIds);
        this.keepOnly(listed);

        const known: ReadonlyMap<string, StukCount> = this.counts$.value;
        const pending: string[] = [...listed].filter(
            dossierId => (known.get(dossierId) ?? UNKNOWN_STUK_COUNT).status === 'unknown'
        );

        if (pending.length === 0) {
            return;
        }

        this.patch(pending.map(dossierId => [dossierId, LOADING_STUK_COUNT]));

        combineLatest([
            this.attachedChildCounts(pending, restrictions),
            this.candidateChildCounts(pending, restrictions)
        ])
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: ([attached, candidates]) => this.onCountsLoaded(pending, attached, candidates),
                error: error => {
                    console.warn(`${StukCountService.LOG_PREFIX} a request failed; the column stays empty.`, error);
                    this.patch(pending.map(dossierId => [dossierId, UNKNOWN_STUK_COUNT]));
                }
            });
    }

    /**
     * The media object ids an export must name explicitly for these ticked dossiers: the engine's own
     * `childOf` walk covers *permanent* children only, so adopted candidates would be missing from the ZIP.
     * Takes record ids, batched across every dossier. Past {@link MAX_CANDIDATES} it errors rather than
     * answering short, a truncated list being the silent data loss this exists to prevent.
     */
    candidateChildIds(dossierIds: string[], restrictions?: Restriction): Observable<MediaObjectId[]> {
        return this.candidateChildren(dossierIds, restrictions).pipe(
            map(candidates => candidates.map(candidate => candidate.getMediaObjectId()))
        );
    }

    /**
     * {@link candidateChildIds}, but the records themselves: the export's analog check needs a candidate's
     * record id and `DocumentAnalog` flag as well as the media object id it is exported by.
     */
    candidateChildren(dossierIds: string[], restrictions?: Restriction): Observable<ApiFragment[]> {
        const dossiers: string[] = [...new Set(dossierIds)];

        if (dossiers.length === 0) {
            return of([]);
        }

        return collectSearchResults(
            (startIndex, nrOfResults) =>
                this.sandbox.search(this.candidateSearch(dossiers, startIndex, nrOfResults, restrictions)),
            StukCountService.MAX_CANDIDATES
        ).pipe(
            map(candidates => {
                if (!candidates) {
                    throw new Error(
                        `The ticked dossiers hold more candidate children than the ${StukCountService.MAX_CANDIDATES}` +
                            ` this resolves, so the export scope cannot be assembled in full.`
                    );
                }

                return candidates;
            })
        );
    }

    /** A count taken under other restrictions answers a different question, so nothing taken before survives. */
    reset(): void {
        this.counts$.next(new Map());
    }

    private keepOnly(dossierIds: ReadonlySet<string>): void {
        const kept: [string, StukCount][] = [...this.counts$.value].filter(([dossierId]) => dossierIds.has(dossierId));

        if (kept.length !== this.counts$.value.size) {
            this.counts$.next(new Map(kept));
        }
    }

    /** Neither half can be filled in from the other, so one untrustworthy answer discards both. */
    private onCountsLoaded(pending: string[], attached: ChildCounts, candidates: ChildCounts): void {
        if (!attached || !candidates) {
            this.patch(pending.map(dossierId => [dossierId, UNKNOWN_STUK_COUNT]));

            return;
        }

        this.patch(
            pending.map(dossierId => [
                dossierId,
                { status: 'ready', count: (attached.get(dossierId) ?? 0) + (candidates.get(dossierId) ?? 0) }
            ])
        );
    }

    /**
     * One facet request, grouping the page's dossiers by the reference code their stukken carry. The facet's
     * values *are* the dossier ids and its counts the children per dossier.
     */
    private attachedChildCounts(dossierIds: string[], restrictions?: Restriction): Observable<ChildCounts> {
        return this.facetDataService
            .getFacets(this.attachedFacetRequest(dossierIds, restrictions))
            .pipe(map(facets => StukCountService.toAttachedCounts(facets)));
    }

    /**
     * A dossier missing from the facet holds no attached stukken — but only if the facet was complete. Under
     * truncation the two cases are indistinguishable, so the whole answer is discarded rather than reported
     * short; the facet asks for one value per dossier on screen, so this should not be reachable.
     */
    private static toAttachedCounts(facets: Facet[]): ChildCounts {
        const facet: Facet | undefined = facets.find(
            candidate => candidate.DottedKey === DottedKey.ReferenceCodesDossier
        );

        if (!facet) {
            console.warn(
                `${StukCountService.LOG_PREFIX} the facets response carries no ${DottedKey.ReferenceCodesDossier} facet` +
                    ` — the engine may not facet this field, or the request never reached that branch.` +
                    ` Keys it did return: ${facets.map(candidate => candidate.DottedKey).join(', ') || '(none)'}.`
            );

            return null;
        }

        if (facet.HasMoreValues) {
            console.warn(
                `${StukCountService.LOG_PREFIX} the ${DottedKey.ReferenceCodesDossier} facet reports more values than` +
                    ` it returned, so a dossier missing from it cannot be told from one holding no stukken.` +
                    ` Counts discarded rather than under-reported.`
            );

            return null;
        }

        return new Map(facet.Values.map(value => [value.Value, value.Count]));
    }

    /**
     * The candidate children of every dossier on the page. The records themselves come back rather than a bare
     * number, which is what makes the sum with the attached side exact instead of an assumption about whether
     * the two states can overlap — and it is why this pages rather than asking for one oversized response.
     */
    private candidateChildCounts(dossierIds: string[], restrictions?: Restriction): Observable<ChildCounts> {
        return collectSearchResults(
            (startIndex, nrOfResults) =>
                this.sandbox.search(this.candidateSearch(dossierIds, startIndex, nrOfResults, restrictions)),
            StukCountService.MAX_CANDIDATES
        ).pipe(
            map(candidates => {
                if (!candidates) {
                    console.warn(
                        `${StukCountService.LOG_PREFIX} the candidate children of this page could not be counted` +
                            ` (cap ${StukCountService.MAX_CANDIDATES}); the column stays empty for this page.`
                    );

                    return null;
                }

                return StukCountService.toCandidateCounts(dossierIds, candidates);
            })
        );
    }

    /**
     * A candidate names its parent by the dossier's *fragment* id, which starts with the dossier's record id —
     * the same relationship the wildcard in the query relies on. A stuk offered to two dossiers on the page
     * counts for both, because expanding either would show it.
     */
    private static toCandidateCounts(dossierIds: string[], candidates: ApiFragment[]): ChildCounts {
        const counts: Map<string, number> = new Map(dossierIds.map(dossierId => [dossierId, 0]));

        candidates.forEach(candidate => {
            const parents: string[] = candidate.metadata.Structural?.Relations?.['IsCandidateChildOf'] ?? [];

            dossierIds
                .filter(dossierId => parents.some(fragmentId => fragmentId.startsWith(dossierId)))
                .forEach(dossierId => counts.set(dossierId, counts.get(dossierId)! + 1));
        });

        return counts;
    }

    /**
     * The non-deprecated branch of `POST /v2/records/facets`, which is selected by the request's *shape*: a
     * structured query with `Source.CustomFacets`, exactly what the facet panel already sends. The branch
     * that accepts a top-level `Q` is the legacy one, and the backend injects its own base record query here —
     * so the count is scoped to what the user may see, by construction.
     */
    private attachedFacetRequest(dossierIds: string[], restrictions?: Restriction): FacetRequest {
        const options: Record<string, FacetValueOptions> = {
            [DottedKey.ReferenceCodesDossier]: {
                // One value per dossier on screen. The API's own default is 10, which would silently leave
                // the last 15 rows of a page without a count.
                DefaultNumberOfValues: dossierIds.length,
                IncludeMissingValue: false
            }
        };

        return {
            Search: { StructuredQuery: this.attachedStructuredQuery(dossierIds, restrictions) },
            Source: { CustomFacets: [DottedKey.ReferenceCodesDossier] },
            // The constraint is redundant for a first page, but it makes this request structurally identical to
            // the single-field one `ShFacetDataService.getProfileFieldFacet` sends — the shape this endpoint is
            // known to answer on these pages — so the only thing left untried is the field itself.
            Value: {
                Options: options,
                Constraints: { [DottedKey.ReferenceCodesDossier]: { StartIndex: 0 } }
            }
        };
    }

    /**
     * The scoping query travels as `Predefined` — a Lucene string, the way page restrictions reach the facet
     * panel — rather than as a structured `AdvancedSearch`. That is what lets it be built by the same builder
     * the children query uses, so the count cannot drift from the rows an expansion reveals.
     */
    private attachedStructuredQuery(dossierIds: string[], restrictions?: Restriction): FacetStructuredQuery {
        const typeGroup: AdvancedSearchGroup = {
            LogicalOperand: LogicalOperand.And,
            Terms: [
                { DottedKey: MetadataField.RecordType, Operand: Operand.Include, Values: [RecordType.Document] },
                // Candidates are counted by the search instead. Excluding them here is what makes summing the
                // two sides exact whether or not a stuk can be attached and a candidate at the same time.
                {
                    DottedKey: MetadataField.RelationsIsCandidateChildOf,
                    Operand: Operand.Exclude,
                    Values: ['*']
                },
                {
                    DottedKey: MetadataField.ReferenceCodesDocument,
                    Operand: Operand.Exclude,
                    Values: [HierarchyService.ANY_VALUE]
                }
            ]
        };

        const parentGroup: AdvancedSearchGroup = {
            LogicalOperand: LogicalOperand.Or,
            Terms: [
                {
                    // Matched exactly: a reference code is a full-length record id, so no wildcard is needed.
                    DottedKey: MetadataField.ReferenceCodesDossier,
                    Operand: Operand.Include,
                    Values: dossierIds
                }
            ]
        };

        const query: ApiQuery = new ApiQuery(StukCountService.toQuery(typeGroup, parentGroup), restrictions);

        return {
            ...new ApiQuery(EmptyStructuredQuery).toApiStructuredQuery(),
            Predefined: [ApiQueryBuilder.queryToLuceneQuery(query)]
        };
    }

    /** The candidate arm of {@link HierarchyService}'s children query, over every dossier on the page at once. */
    private candidateSearch(
        dossierIds: string[],
        startIndex: number,
        nrOfResults: number,
        restrictions?: Restriction
    ): RecordSearch {
        const typeGroup: AdvancedSearchGroup = {
            LogicalOperand: LogicalOperand.And,
            Terms: [{ DottedKey: MetadataField.RecordType, Operand: Operand.Include, Values: [RecordType.Document] }]
        };

        const parentGroup: AdvancedSearchGroup = {
            LogicalOperand: LogicalOperand.Or,
            Terms: [
                {
                    // The wildcard is needed here and only here: the relation stores a fragment id.
                    DottedKey: MetadataField.RelationsIsCandidateChildOf,
                    Operand: Operand.Include,
                    Values: dossierIds.map(dossierId => `${dossierId}*`)
                }
            ]
        };

        const query: ApiQuery = new ApiQuery(StukCountService.toQuery(typeGroup, parentGroup), restrictions);

        return {
            // The shared builder rather than `LuceneHelper`, as in `HierarchyService`: only this one makes the
            // restrictions a *required* clause instead of an optional one.
            Q: ApiQueryBuilder.queryToLuceneQuery(query),
            StartIndex: startIndex,
            NrOfResults: nrOfResults
        };
    }

    private static toQuery(...groups: AdvancedSearchGroup[]): StructuredQuery {
        return {
            ...EmptyStructuredQuery,
            AdvancedSearch: { LogicalOperand: LogicalOperand.And, Groups: groups }
        };
    }

    private patch(entries: [string, StukCount][]): void {
        const updated: Map<string, StukCount> = new Map(this.counts$.value);
        entries.forEach(([dossierId, count]) => updated.set(dossierId, count));

        this.counts$.next(updated);
    }
}
